<!--
# `InstantWinAnimation.vue`

## Descrizione
`InstantWinAnimation.vue` è un componente Vue.js che gestisce l'animazione e la logica di un concorso a premi istantaneo. Il componente include diverse sezioni che rappresentano vari stati del concorso, come "open", "played", "won", ecc.

## Proprietà (`props`)
- `accessToken` (String): Token di accesso per le API.
- `apiUrl` (String): URL dell'API per le chiamate di gioco.
- `userId` (String): ID dell'utente.
- `contestData` (Object): Dati del concorso.
- `loader` (String): URL dell'immagine del loader.
- `reward` (String): Premio del concorso.
- `badge_icon` (String): Icona del badge (opzionale, default: '0').
- `popupLinkRedirect` (String): URL di reindirizzamento per il popup.

## Dati (`data`)
- `loading` (Boolean): Stato di caricamento.
- `current_state` (String): Stato attuale del concorso.
- `current_contest` (Object): Dati del concorso corrente.
- `next_contest_index` (Number): Indice del prossimo concorso.
- `attempts_left` (Number): Tentativi rimanenti.
- `attempts_done` (Number): Tentativi effettuati.
- `play_data` (Object): Dati di gioco.
- `modal_id` (String): ID del modal.
- `modal` (Object): Stato del modal.
- `acceptances` (Object): Accettazioni di regolamento e privacy.
- `token_keyword_contests` (Array): Parole chiave per i concorsi con token nell'URL.
- `flg_badge_icon` (Boolean): Flag per l'icona del badge.
- `pre_release` (Boolean): Stato di pre-release.

## Metodi (`methods`)
- `statsClick(contest)`: Gestisce il click sul concorso per le statistiche.
- `showAcceptanceModal()`: Mostra il modal di accettazione.
- `play()`: Esegue il gioco.
- `doesContestHaveTokenInUrl()`: Verifica se il concorso ha un token nell'URL.
- `chooseTypeAndElement()`: Sceglie il tipo e l'elemento in base all'utente.

## Computed Properties (`computed`)
- `sliderImages()`: Restituisce le immagini dello slider.
- `user_can_play()`: Verifica se l'utente può giocare.
- `is_last_day()`: Verifica se è l'ultimo giorno del concorso.
- `countdown()`: Restituisce il countdown del concorso.
- `getExternalURL()`: Restituisce l'URL esterno del concorso.

## Watchers (`watch`)
- `attempts_left`: Osserva i cambiamenti nei tentativi rimanenti.
- `attempts_done`: Osserva i cambiamenti nei tentativi effettuati.
- `current_state`: Osserva i cambiamenti nello stato attuale.
- `user`: Osserva i cambiamenti nell'utente e aggiorna il tipo e l'elemento.

## Componenti Figli
- `InstantWinBasicAttempts`
- `Countdown`
- `LoaderOverlay`
- `BasicModal`
- `ErrorModal`
- `FeaturedCarousel`
- `ShareButtons`
- `ListWinners`

## Esempio di Utilizzo
```vue
<InstantWinAnimation
  :accessToken="accessToken"
  :apiUrl="apiUrl"
  :userId="userId"
  :contestData="contestData"
  :loader="loader"
  :reward="reward"
  :badge_icon="badge_icon"
  :popupLinkRedirect="popupLinkRedirect"
/>
-->

<template>
  <div class="vue iw-basic">

    <LoaderOverlay v-if="loading" :image="loader" :show_winner="show_winner" other-class="timparty-loader-overlay-gradient"/>
    <div
      class="tp-featured iw-container"
      :class="{'legal-notes-padding' : contestData.legalNotes !== ''}"
    >

      <!-- HEADER / BEGIN -->
      <div class="iw-header">
        <div class="row row-eq-height">
          <div class="col-md-6  text-right">
            <div class="iw-info half-container-md p-4 text-left d-flex">

              <div>
                <!-- STATE : OPEN / BEGIN -->
                <div v-if="current_state == 'open' || current_state == 'incoming' || current_state == null">
                  <h1 class="tp-title iw-title text-white" v-html="current_contest.title"></h1>
                  <div class="tp-description iw-description text-white d-none d-md-block mt-4"
                       v-html="current_contest.description"></div>


                  <div v-if="contestData.regulationUrl" class="small mt-4">Scarica qui il <a
                    :href="contestData.regulationUrl" target="_blank"
                    class="text-white font-weight-bold">Regolamento</a></div>


                  <div v-if="legal_notes" class="legal-notice mt-4">
                    <div v-html="legal_notes"></div>
                  </div>

                  <div class="mt-4">
                    <share-buttons></share-buttons>
                  </div>
                </div>
                <!-- STATE : OPEN / END -->


                <!-- STATE : RESULT WIN / BEGIN -->
                <!-- STATE : RESULT WIN / END -->


                <!-- STATE : RESULT LOSE / BEGIN -->
                <!-- STATE : RESULT LOSE / BEGIN -->


                <!-- STATE : PLAYED / BEGIN -->
                <div v-else-if="current_state === 'played' || (current_state === 'result_lose' && attempts_left === 0)" class="grazie-partecipato-block">
                  <h1 class="tp-title iw-title text-white">
                    {{ __('Hai già partecipato a questo concorso') }}
                  </h1>

                  <div class="mt-4">
                    <share-buttons></share-buttons>
                  </div>
                </div>
                <!-- STATE : PLAYED / BEGIN -->


                <!-- STATE : CONTEST WAITING FOR EXTRACTION / BEGIN -->
                <div v-else-if="current_state == 'waiting_for_extraction' ">
                  <h1 class="tp-title iw-title text-white" v-html="current_contest.title"></h1>
                  <div class="tp-description iw-description text-white d-none d-md-block mt-4">

                    <h5 class="text-red font-weight-bold mt-5">{{ __('Il concorso è terminato') }}</h5>

                    <p class="mt-4">
                      <span v-if="contestData.finalExtractionDate">L'estrazione finale avverrà entro il <span
                        class="text-yellow font-weight-bold">{{
                          formatTimestamp(contestData.finalExtractionDate)
                        }}</span></span><br>
                    </p>

                  </div>


                </div>
                <!-- STATE : CONTEST WAITING FOR EXTRACTION / END -->


                <!-- STATE : WON / BEGIN -->
                <div v-else-if="current_state == 'won' ">
                  <h1 v-if="reward" class="tp-title iw-title text-white">
                    {{ __('Complimenti hai vinto ') }} {{ reward }}!
                  </h1>
                  <h1 v-else class="tp-title iw-title text-white">
                    {{ __('Hai vinto questo concorso') }}
                  </h1>

                  <div class="mt-4">
                    <share-buttons></share-buttons>
                  </div>
                </div>
                <!-- STATE : WON / BEGIN -->


                <!-- STATE : CONTEST ENDED / BEGIN -->
                <div v-else-if="current_state == 'ended' ">
                  <h1 class="tp-title iw-title text-white">
                    {{ __('Concorso terminato') }}
                  </h1>


                  <div class="d-none d-md-block">
                    <div class="rewards-summary mt-5">
                      <div class="d-flex align-items-center">

                        <div class="pr-3" v-if="current_contest_rewards_name">
                          <img :src="modulePath('/js/src/assets/images/icon-teal-gift-closed.png')"
                               class="reward-gift-icon">
                        </div>

                        <div class="pr-3" v-if="current_contest_rewards_name">
                          <div class="reward-name" v-html="current_contest_rewards_name"></div>
                        </div>

                        <div class="pr-3 "
                             v-if="!pre_release && current_contest_rewards_name && contestData.badgeAvailable && flg_badge_icon">
                          <img :src="modulePath('/js/src/assets/images/icon-teal-plus.png')" class="reward-plus-icon">
                        </div>

                        <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                          <div class="reward-name"><img :src="modulePath('/js/src/assets/images/icon-teal-badge.png')"
                                                        class="reward-badge-icon"> {{ contestData.badgeAvailable }}
                            {{ __('badge') }}
                          </div>
                        </div>

                      </div>
                    </div>

                    <div class="small mt-4">Scarica qui il <a :href="contestData.regulationUrl" target="_blank"
                                                              class="text-white font-weight-bold">Regolamento</a></div>

                    <div class="mt-4">
                      <a href="#" @click="showWinners"
                         class="font-weight-bold btn text-primary btn-secondary">{{ __('SCOPRI TUTTI I VINCITORI') }}
                        &nbsp;&gt;</a>
                    </div>

                    <div class="mt-4">
                      <share-buttons></share-buttons>
                    </div>

                  </div>
                </div>
                <!-- STATE : CONTEST ENDED / END -->


                <!-- STATE : ALL / BEGIN -->
                <div v-else>
                  <h1 class="tp-title iw-title text-white" v-html="current_contest.title"></h1>
                  <div class="tp-description iw-description text-white d-none d-md-block mt-4"
                       v-html="current_contest.description"></div>

                  <div v-if="contestData.regulationUrl" class="small mt-4">Scarica qui il <a
                    :href="contestData.regulationUrl" target="_blank"
                    class="text-white font-weight-bold">Regolamento</a></div>

                  <div class="mt-4">
                    <share-buttons></share-buttons>
                  </div>
                </div>
                <!-- STATE : ALL / END -->


              </div>
            </div>
          </div>
          <div class="col-md-6 iw-carousel-container p-0">

            <!-- FEATURED CAROUSEL - BEGIN -->
            <featured-carousel :images='sliderImages'></featured-carousel>
            <!-- FEATURED CAROUSEL - END -->

          </div>
        </div>
      </div>
      <!-- HEADER / END -->


      <!-- ------------------------------------ -->


      <!-- ACTIONS / BEGIN  -->

      <div class="iw-actions-bar-container" v-if="current_state">
        <div class="container p-0">
          <div class="iw-actions-bar">

            <!-- STATE : INCOMING / BEGIN -->
            <div v-if="current_state == 'incoming'">

              <div class="row no-gutters">
                <div class="col-md-9  order-2 order-md-1 p-3">
                  <div class="d-flex align-items-center rewards-summary">

                    <div class="pr-3 " v-if="current_contest_rewards_name_short">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-gift-closed.png')"
                           class="reward-gift-icon">
                    </div>
                    <div class="pr-3" v-if="current_contest_rewards_name_short">
                      <div class="reward-label">{{ __('Prova a vincere') }}</div>
                      <div class="reward-name" v-html="current_contest_rewards_name_short"></div>
                    </div>

                    <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-plus.png')" class="reward-plus-icon">
                    </div>
                    <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-badge.png')" class="reward-badge-icon"/>
                    </div>
                    <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <div class="reward-label">{{ __('Ottieni') }}</div>
                      <div class="reward-name"> {{ contestData.badgeAvailable }} {{ __('badge') }}</div>
                    </div>
                  </div>

                </div>
                <div class="col-md-3 order-1 order-md-2 d-flex align-items-center justify-content-center p-3">
                  <div v-if="countdown.time">
                    <countdown class="iw-countdown" :ts-end="countdown.time" :heading="countdown.label"></countdown>
                  </div>
                </div>

              </div>

              <div class="tp-description iw-description text-white  d-block d-md-none py-4 px-3"
                   v-html="current_contest.description"></div>

            </div>
            <!-- STATE : INCOMING / END -->


            <!-- STATE : OPEN / BEGIN -->

            <!-- STATE : OPEN / END -->

            <!-- STATE : RESULT WIN / BEGIN -->
            <div v-else-if="current_state == 'result_win'" class="">

              <div class="row no-gutters">
                <div class="col-md-9">
                  <div class="d-flex align-items-start ">
                    <div class="p-3">
                      <h5>{{ __('Per ricevere il tuo premio clicca su "Accetta il premio"') }}</h5>
                      <p>{{
                          __("Il premio verrà spedito all'indirizzo che indicherai nel form che verrà mostrato di seguito.")
                        }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-3 d-flex align-items-center">
                  <form :action="apiUrl" method="post" class="w-100 p-3">
                    <input type="hidden" name="action" value="gotoRewardForm">
                    <input type="hidden" name="access_token" :value="accessToken">
                    <input type="hidden" name="contest_code" :value="contestData.code">
                    <input type="hidden" name="play_data" :value='JSON.stringify(play_data)'>
                    <input type="submit" class="btn btn-primary btn-block btn-lg" :value="__('Accetta il premio')">
                  </form>
                </div>
              </div>

            </div>
            <!-- STATE : RESULT WIN  / END -->

            <!-- STATE : RESULT LOSE / BEGIN -->
            <!-- STATE : RESULT LOSE  / END -->


            <!-- STATE : PLAYED / BEGIN -->
            <div v-else-if="current_state == 'played'" class="">

              <div class="row">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="p-3">
                      <h5 class="font-weight-bold">{{ __('Non mollare, tenta la fortuna con altre linee o con gli altri concorsi di TIM Party!') }}</h5>
                      <p>{{
                          __('Se hai più linee, gioca con tutte per usare tutti i tentativi e non perderti gli altri concorsi di TIM Party!')
                        }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 d-flex align-items-center justify-content-end">
                  <div class="p-3 w-100">
                    <a v-if="!pre_release" class="btn btn-action btn-lg btn-block  cta-generic"
                       href="/gioca-e-vinci">{{ __('Scopri altri concorsi') }}</a>
                    <a v-else class="btn btn-action btn-lg btn-block"
                       href="https://www.timparty.tim.it">{{ __('Torna alla homepage') }}</a>
                  </div>
                </div>
              </div>

            </div>
            <!-- STATE : PLAYED  / END -->


            <!-- STATE : WAITING FOR EXTRACTION / BEGIN -->
            <div v-else-if="current_state == 'waiting_for_extraction'">
              <div class="d-block d-md-none p-3 mb-5">

                <h5 class="text-red font-weight-bold mt-5">{{ __('Il concorso è terminato') }}</h5>

                <p class="mt-4">
                  <span v-if="contestData.finalExtractionDate">L'estrazione finale avverrà il <span
                    class="text-yellow font-weight-bold">{{
                      formatTimestamp(contestData.finalExtractionDate)
                    }}</span></span><br>
                </p>
              </div>

              <div class="row">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="p-3">
                      <h5 class="font-weight-bold text-red">
                        {{ __('Se hai partecipato al Concorso, torna presto per scoprire i nomi dei vincitori.') }}</h5>
                      <p>{{
                          __('Nel frattempo, continua a giocare e fatti trovare pronto per il prossimo Concorso! I vincitori saranno contattati direttamente dal nostro Servizio Clienti, non appena verrà effettuata l’estrazione finale. ')
                        }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4  d-flex align-items-center justify-content-end">
                  <div class="p-3 w-100">
                    <a href="/gioca-e-vinci"
                       class="btn btn-action btn-lg btn-block cta-generic">{{ __('Scopri altri concorsi') }}</a>
                  </div>
                </div>

              </div>

            </div>
            <!-- STATE : WAITING FOR EXTRACTION / END -->


            <!-- STATE : CONTEST ENDED / BEGIN -->
            <div v-else-if="current_state == 'ended'" class="">
              <div class="d-block d-md-none p-3 mb-5">
                <h5 class="text-red">{{ __('Il concorso è terminato') }}</h5>
                <div class="rewards-summary mt-4">
                  <div class="d-flex align-items-center">

                    <div class="pr-3" v-if="current_contest_rewards_name_short">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-gift-closed.png')"
                           class="reward-gift-icon">
                    </div>

                    <div class="pr-3" v-if="current_contest_rewards_name_short">
                      <div class="reward-name" v-html="current_contest_rewards_name_short"></div>
                    </div>

                    <div class="pr-3 "
                         v-if="!pre_release && current_contest_rewards_name_short && contestData.badgeAvailable">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-plus.png')" class="reward-plus-icon">
                    </div>

                    <div class="pr-3" v-if="!pre_release &&contestData.badgeAvailable">
                      <div class="reward-name"><img :src="modulePath('/js/src/assets/images/icon-teal-badge.png')"
                                                    class="reward-badge-icon"> {{ contestData.badgeAvailable }}
                        {{ __('badge') }}
                      </div>
                    </div>


                  </div>
                </div>


                <div class="mt-5">
                  <a href="#" @click="showWinners"
                     class="font-weight-bold btn text-primary btn-secondary">{{ __('SCOPRI TUTTI I VINCITORI') }} &nbsp;&gt;</a>
                </div>
              </div>


              <div class="row">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="p-3">
                      <h5 v-if="!pre_release" class="font-weight-bold">
                        {{ __('Non mollare, tenta la fortuna con altre linee o con gli altri concorsi di TIM Party!') }}</h5>
                      <p v-if="!pre_release">{{
                          __('Se hai più linee, gioca con tutte per usare tutti i tentativi e non perderti gli altri concorsi di TIM Party!')
                        }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 d-flex align-items-center justify-content-end">
                  <div class="p-3 w-100">
                    <a v-if="!pre_release" class="btn btn-action btn-lg btn-block cta-generic"
                       href="/gioca-e-vinci">{{ __('Scopri altri concorsi') }}</a>
                    <a v-else class="btn btn-action btn-lg btn-block"
                       href="https://www.timparty.tim.it">{{ __('Torna alla homepage') }}</a>
                  </div>
                </div>
              </div>

            </div>
            <!-- STATE : CONTEST ENDED  / END -->


            <!-- STATE : WON / BEGIN -->
            <div v-else-if="current_state == 'won'" class="">


              <div class="row no-gutters">
                <div class="col-md-6 p-3">
                  <div class="d-flex align-items-center rewards-summary">

                    <div class="pr-3 " v-if="play_data_rewards_name">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-gift-open.png')" class="reward-gift-icon">
                    </div>
                    <div class="pr-3" v-if="play_data_rewards_name">
                      <div class="reward-label">{{ __('Hai vinto') }}</div>
                      <div class="reward-name" v-html="play_data_rewards_name"></div>
                    </div>

                    <div class="pr-3"
                         v-if="play_data_rewards_name && !pre_release && play_data.badgeAcquired && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-plus.png')" class="reward-plus-icon">
                    </div>
                    <div class="pr-3" v-if="!pre_release && play_data.badgeAcquired && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-badge.png')" class="reward-badge-icon"/>
                    </div>
                    <div class="pr-3" v-if="!pre_release && play_data.badgeAcquired && flg_badge_icon">
                      <div class="reward-label">{{ __('Hai ottenuto') }}</div>
                      <div class="reward-name"> {{ play_data.badgeAcquired }} {{ __('badge') }}</div>
                      <!--<div class="reward-name"> 1 {{ __('badge') }}</div>-->
                    </div>
                  </div>

                </div>
                <div class="col-md-3 d-flex align-items-center justify-content-center p-3" v-if="contestData.endDate">
                  <div>{{ __('Non hai ricevuto il premio? Contatta l’assistenza.') }}</div>
                </div>
                <div class="col-md-3 d-flex align-items-top">
                  <div class="w-100 p-3">
                    <a v-if="!pre_release" class="btn btn-primary btn-lg btn-block"
                       href="/come-contattarci">{{ __('Contattaci') }}</a>
                  </div>
                </div>
              </div>

            </div>
            <!-- STATE : WON  / END -->

            <!-- STATE : LOSE AND 0 ATTEMPTS LEFT / BEGIN -->
            <div v-else-if="current_state === 'result_lose' && attempts_left === 0" class="">

              <div class="row">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="p-3">
                      <h5 class="font-weight-bold">{{ __('Non mollare, tenta la fortuna con altre linee o con gli altri concorsi di TIM Party!') }}</h5>
                      <p>{{
                          __('Se hai più linee, gioca con tutte per usare tutti i tentativi e non perderti gli altri concorsi di TIM Party!')
                        }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 d-flex align-items-center justify-content-end">
                  <div class="p-3 w-100">
                    <a v-if="!pre_release" class="btn btn-action btn-lg btn-block  cta-generic"
                       href="/gioca-e-vinci">{{ __('Scopri altri concorsi') }}</a>
                    <a v-else class="btn btn-action btn-lg btn-block"
                       href="https://www.timparty.tim.it">{{ __('Torna alla homepage') }}</a>
                  </div>
                </div>
              </div>

            </div>
            <!-- STATE : LOSE AND 0 ATTEMPTS LEFT  / END -->

            <!-- STATE : RESULT OTHER  / START -->

            <div v-else>

              <div class="row no-gutters">
                <div class="col-md-6 order-2 order-md-1 p-3">
                  <div class="d-flex align-items-center rewards-summary">

                    <div class="pr-3 " v-if="current_contest_rewards_name_short">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-gift-closed.png')"
                           class="reward-gift-icon">
                    </div>
                    <div class="pr-3" v-if="current_contest_rewards_name_short">
                      <div class="reward-label">{{ __('Prova a vincere') }}</div>
                      <div class="reward-name" v-html="current_contest_rewards_name_short"></div>
                    </div>

                    <div class="pr-3"
                         v-if="current_contest_rewards_name_short && !pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-plus.png')" class="reward-plus-icon">
                    </div>
                    <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <img :src="modulePath('/js/src/assets/images/icon-teal-badge.png')" class="reward-badge-icon"/>
                    </div>
                    <div class="pr-3" v-if="!pre_release && contestData.badgeAvailable && flg_badge_icon">
                      <div class="reward-label">{{ __('Ottieni') }}</div>
                      <div class="reward-name"> {{ contestData.badgeAvailable }} {{ __('badge') }}</div>
                    </div>
                  </div>

                </div>
                <div class="col-md-3  order-1 order-md-2 d-flex align-items-center justify-content-center p-3">
                  <countdown v-if="countdown.time && !pre_release" class="iw-countdown" :ts-end="countdown.time"
                             :heading="countdown.label"></countdown>
                </div>
                <div class="col-md-3 order-3 d-flex align-items-top">
                  <div class="w-100">
                    <div class="iw-play-container">
                      <div class="iw-btn-play-container">
                        <button class="btn btn-primary btn-lg btn-block iw-btn-play"
                                :class="{'btn-open-app-modal': !contestData.matchChannel}"
                                @click.prevent="showAcceptanceModal">{{
                            contestData.matchChannel ? current_contest.buttonLabel ? current_contest.buttonLabel : __('Gioca') : __('ACCEDI DA APP MY TIM E PARTECIPA')
                          }}
                        </button>
                      </div>
                      <instant-win-basic-attempts
                        v-if="current_contest.type !== 'EXTERNAL' && attempts_left && current_contest.type !== 'FINAL_EXTR'"
                        v-bind:attempts_left="attempts_left"></instant-win-basic-attempts>
                    </div>
                  </div>
                </div>
              </div>

              <div class="tp-description iw-description text-white  d-block d-md-none py-4 px-3"
                   v-html="current_contest.description"></div>

            </div>

            <!-- STATE : RESULT OTHER  / END -->

          </div>
        </div>
      </div>
    </div>

    <!-- ACTIONS / END  -->
    <!--      <div v-if="contestData.legalNotes !== ''" class="legal-notes">-->
    <!--        <div class="container pb-5 pt-2 small">-->
    <!--          {{ contestData.legalNotes }}-->
    <!--        </div>-->
    <!--      </div>-->
    <div class="bg-warning p-4" v-if="environment == 'dev'">
      <div class="container">
        <h5>Development tools</h5>

        <div class="form-inline my-2">
          <select v-model="current_state" class="form-control">
            <option value="">---</option>
            <option value="incoming">incoming</option>
            <option value="open">open</option>
            <option value="result_win">result_win</option>
            <option value="result_lose">result_lose</option>
            <option value="played">played</option>
            <option value="waiting_for_extraction">waiting_for_extraction</option>
            <option value="won">won</option>
            <option value="ended">ended</option>
          </select>


          <button class="btn btn-danger ml-4" @click.prevent="dispatchUpdateBadges">Update Badge</button>

          <div class="form-group ml-4">
            <div class="form-checkbox">
              <input class="form-checkbox-input" type="checkbox" v-model="pre_release">
              <label class="form-checkbox-label">Pre Release</label>

            </div>
          </div>

          <div class="form-group ml-4">
            <div class="form-checkbox">
              <input class="form-checkbox-input" type="checkbox" v-model="loading">
              <label class="form-checkbox-label">Loading</label>

            </div>
          </div>
        </div>
      </div>

    </div>
    <!-- MODAL ACCEPTANCE / START -->
    <basic-modal :modal-id="modal_id" modal-class="modal-md" class="modal-acceptance" :show="modal.show"
                 v-on:modalClose="modal.show = false"
                 :modal-style="'position: relative !important;'">


      <div v-if="modal.state == 'acceptance'">
        <div class="modal-body">

          <!--  REGULATION ACCEPTANCE / START -->
          <div v-if="contestData.regulationInfo != undefined && contestData.regulationInfo != ''" class="mb-4">

            <label for="contest-regulation">{{ __('Regolamento') }}</label>
            <div class="textarea-acceptance" id="contest-regulation" v-html="contestData.regulationInfo"></div>

            <div class="custom-control custom-checkbox mt-1">
              <input type="checkbox" class="custom-control-input" id="regulation-acceptance"
                     v-model="acceptances.regulation">
              <label class="custom-control-label" for="regulation-acceptance">{{ __('Accetto il regolamento') }}</label>
            </div>
          </div>
          <div v-else-if="contestData.regulationUrl != undefined && contestData.regulationUrl != ''">

            <label for="contest-regulation">{{ __('Regolamento') }}</label>

            <div class="custom-control custom-checkbox mt-1">
              <input type="checkbox" class="custom-control-input" id="regulation-acceptance"
                     v-model="acceptances.regulation">
              <label class="custom-control-label" for="regulation-acceptance">{{ __('Accetto il ') }} <a
                :href="contestData.regulationUrl" target="_blank">{{ __('Regolamento ') }}</a></label>
            </div>

          </div>
          <!--  REGULATION ACCEPTANCE / END -->


          <!--  PRIVACY ACCEPTANCE / START -->
          <div v-if="contestData.privacyInfo != undefined && contestData.privacyInfo != ''" class="mb-4">

            <!--
            <h3 for="contest-privacy">{{ __('Informativa Privacy') }}</h3>
            <div class="textarea-acceptance"  id="contest-privacy" v-html="contestData.privacyInfo"></div>
            -->

            <div class="custom-control custom-checkbox mt-4">
              <input type="checkbox" class="custom-control-input" id="privacy-acceptance" v-model="acceptances.privacy">
              <label class="custom-control-label"
                     for="privacy-acceptance">{{ __('Ho preso visione dell\'informativa privacy') }}</label><a
              v-if="contestData.privacyInfo" target="__blank" :href="contestData.privacyInfo"
              class="icon-badge-info ml-1"><img
              :src="modulePath('js/src/assets/images/icon-black-info-square.png')"></a>
            </div>

          </div>
          <!--  PRIVACY ACCEPTANCE / END -->

        </div>
        <div class="modal-footer bg-primary  p-4 mt-auto">
          <button class="btn btn-light btn-lg text-uppercase" @click="play" :disabled="!user_can_play">
            {{ __('Gioca Ora!') }}
          </button>
        </div>
      </div>
      <!--
      <div v-else-if="modal.state == 'share'">
          <div class="modal-body text-center p-5">
            <h4>{{ __('Condividi') }}</h4>

            <share-buttons></share-buttons>

          </div>
      </div>
      -->
      <div v-else-if="modal.state == 'winners'">
        <div class="modal-body text-center p-5">
          <h4>{{ __('Ecco i vincitori del concorso') }}</h4>


          <list-winners :winners="winners"></list-winners>


          <button @click.prevent="modal.show = false" class="btn btn-primary btn-lg">{{ __('CHIUDI') }}</button>


        </div>
      </div>
      <div v-else-if="modal.state == 'matchChannelFalse'">
        <div class="modal-body text-center p-5" style="background-color:#0033a1">
          <div>
            <h4 class="text-white">{{ __('PER PARTECIPARE AL CONCORSO ACCEDI DA APP MyTIM') }}</h4>
            <a class="p-2 text-white bg-dark border border-secondary" :href="popupLinkRedirect"
               target="_blank">{{ __('PROSEGUI DA APP MyTIM') }}</a>
          </div>
        </div>
      </div>

      <div v-else-if="modal.state === 'loseModal'">
        <div class="modal-body text-center p-5">
          <div class="timparty-modal-play">
            <h4 class="mb-0 pb-0">{{ __('PURTROPPO QUESTA VOLTA NON HAI VINTO.') }}</h4>

            <template v-if="attempts_left > 0">
              <p>Riprovaci subito, {{ attempts_left === 1 ? 'ti rimane ' : 'ti rimangono ' }}
                <span>{{ attempts_left }}</span>
                {{ attempts_left === 1 ? ' tentativo.' : ' tentativi.' }}
              </p>
              <div class="iw-play-container">
                <div class="iw-btn-play-container">
                  <button class="btn btn-primary btn-lg btn-block iw-btn-play"
                          :class="{'btn-open-app-modal': !contestData.matchChannel}"
                          @click.prevent="showAcceptanceModal">{{
                      contestData.matchChannel ? current_contest.buttonLabel ? current_contest.buttonLabel : __('Gioca') : __('ACCEDI DA APP MY TIM E PARTECIPA')
                    }}
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <p v-if="contestData.code.match(/PARTY_MERC_.*/)">{{ __('Torna mercoledì prossimo per giocare  di nuovo e tentare la fortuna con TIM Party!') }}</p>
              <a class="btn btn-primary btn-lg btn-block iw-btn-play" href="/homepage">{{
                  __('Torna alla homepage')
                }}</a>
            </template>
          </div>
        </div>
      </div>

      <div v-else-if="modal.state === 'winModal'">
        <div class="modal-body text-center p-5">
          <div class="timparty-modal-play">
            <h4 class="text-white">{{ __('COMPLIMENTI!') }}</h4>
            <p>Hai vinto <span v-html="play_data_rewards_name"></span></p>

            <form :action="apiUrl" method="post" class="w-100 p-3">
              <input type="hidden" name="action" value="gotoRewardForm">
              <input type="hidden" name="access_token" :value="accessToken">
              <input type="hidden" name="contest_code" :value="contestData.code">
              <input type="hidden" name="play_data" :value='JSON.stringify(play_data)'>
              <input type="submit" class="btn btn-primary btn-lg btn-block iw-btn-play"
                     :value="__('ACCETTA IL PREMIO')">
            </form>
          </div>
        </div>
      </div>


    </basic-modal>
    <!-- MODAL ACCEPTANCE / END -->


    <!-- MODAL ERROR / BEGIN -->
    <ErrorModal :error="error" v-on:modalClose="error = null"/>
    <!-- MODAL ERROR / END -->

  </div>
</template>

<script>

/* eslint-disable */


import InstantWinBasicAttempts  from "@/components/InstantWinBasicAttempts";
import _, {isNull, isUndefined} from 'lodash';
import TimPartyMixin            from '../mixins/TimPartyMixin';
import TimPartyContestMixin    from '../mixins/TimPartyContestMixin';

import BasicModal       from './BasicModal.vue'
import ErrorModal       from './ErrorModal.vue'
import Countdown        from './Countdown.vue'
import LoaderOverlay    from './LoaderOverlay.vue'
import FeaturedCarousel from './FeaturedCarousel.vue'
import ShareButtons     from './ShareButtons.vue'
import ListWinners      from './ListWinners.vue'

export default {
  name: 'InstantWinAnimation',
  mixins: [TimPartyMixin, TimPartyContestMixin],

  components: {
    InstantWinBasicAttempts,
    Countdown,
    LoaderOverlay,
    BasicModal,
    ErrorModal,
    FeaturedCarousel,
    ShareButtons,
    ListWinners
  },

  props: {
    accessToken: String,
    apiUrl: String,
    userId: String,
    contestData: Object,
    loader: String,
    reward: String,
    badge_icon: {
      type: String,
      required: false,
      default: '0'
    },
    popupLinkRedirect: ''

  },
  data: function () {

    return {
      loading: false,
      current_state: null,

      current_contest: {
        title: 'Concorso non disponibile',
        label: 'Gioca',
        label_get_reward: 'Riscuoti la tua vincita',
        message_loss: 'Siamo spiacenti, questa volta non hai vinto. Ritenta la prossima volta!'
      },
      next_contest_index: 0,
      attempts_left: 0,
      attempts_done: 0,

      play_data: {},

      modal_id: 'modal-iw-basic',

      modal: {
        id: 'modal-iw-basic',
        show: false,
        class_name: 'modal-lg',
        error: null,
        state: null
      },

      acceptances: {
        regulation: false,
        privacy: false
      },
      token_keyword_contests: [
        'FANTACALCIO',
        'CALCIO'
      ],
      flg_badge_icon: true,

      pre_release: false
    };
  },
  mounted: function () {
    let self            = this;
    this.flg_badge_icon = this.badge_icon === "0";

    this.setApiUrl(this.apiUrl);

    if (this.contestData != undefined) {


      if (this.contestData.extra.status != undefined) {
        this.current_state = this.contestData.extra.status;
      }

      if (this.contestData.playResult != undefined) {
        this.play_data = this.contestData.playResult;
      }

      this.gotoNextContest();
    }
  },

  methods: {

    statsClick: function (contest) {
      this.gtmEvent({
        event: 'redirectContestClick',
        eventCategory: 'Click',
        eventAction: 'Contest Click',
        eventLabel: this.stripTags(contest.code)
      });


      this.aaEvent('click', {
        form: {
          banner: 'banner slider-' + this.stripTags(banner.title)
        }
      });
    },

    showAcceptanceModal: function () {
      if (this.contestData.matchChannel === true) {
        if (this.current_contest.type === 'EXTERNAL') {
          let token_url_prefix = 'token';

          const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop) => searchParams.get(prop)
          });
          let debug    = params.debug === 'nimda';
          if (debug) {
            console.log('this.contestData.externalUrl', this.contestData.externalUrl + '?' + token_url_prefix + '=' + this.accessToken);
          }
          else {
            // window.location.href = this.contestData.externalUrl + '?' + token_url_prefix + '=' + this.accessToken;
            window.open(this.contestData.externalUrl + '?' + token_url_prefix + '=' + this.accessToken, '_blank');
          }
        }
        else if (this.attempts_done > 0 || this.contestData.numUserPlayed > 0) {
          this.play();
        }
        else {
          this.modal.show  = true;
          this.modal.state = 'acceptance';
        }
      }
      else {
        this.modal.show        = true;
        this.modal.state       = 'matchChannelFalse';
        this.popupLinkRedirect = 'https://www.tim.it/app-mytim';
      }

    },

    play: function () {

      jQuery('body').removeClass('modal-open');

      let self = this;

      this.aaEvent('click', {
        form: {
          click: "Partecipa"
        }
      });

      let payload = {
        action: 'play',
        access_token: this.accessToken,
        contest_id: this.current_contest.id,
        play_data: {
          email: 'fake@email.com',
          selections: [{
            id: 0,
            text: ''
          }]
        }
      };

      let current_contest = this.current_contest;

      this.loading    = true;
      self.modal.show = false;

      this.callApiDelayed(payload, 4000, function (response) {
        self.loading = false;

        if (!response.success) {
          self.current_state = null;

          self.showError(response.data);
        }
        else if (response.data.win != undefined) {

          if (current_contest.type == 'FINAL_EXTR') {
            self.current_state = 'played';
          }
          else {
            self.current_state = response.data.win ? 'result_win' : 'result_lose';

            if (self.current_state === 'result_win') {
              self.modal.show  = true;
              self.modal.state = 'winModal';
            }
            else {
              self.modal.show  = true;
              self.modal.state = 'loseModal';
            }

          }
          self.attempts_left = self.attempts_left - 1;
          self.attempts_done = self.attempts_done + 1;
          self.play_data     = response.data;

          self.gtmEvent({
            event: 'giocaEvinci',
            eventCategory: 'Gioca e Vinci',
            eventAction: response.data.win ? 'Success' : 'Failure',
            eventLabel: self.current_contest.title
          });

          // self.dispatchUpdateBadges();
        }
        else {
          self.current_state = null;
        }
      });
    },

    doesContestHaveTokenInUrl: function () {
      let self = this;

      let codeSeparated = this.contestData.code.split('_');
      if (self.token_keyword_contests.includes(codeSeparated[0])) {
        return true;
      }
      else {
        return false;
      }
    },

    chooseTypeAndElement: function () {
      this.$nextTick(function () {
        if (typeof this.user.type !== 'undefined') {
          if (this.user.type === 'FIX' || this.user.type.toUpperCase() === 'FISSO') {
            document.querySelectorAll('.line-fix').forEach(function (element) {
              element.classList.remove("d-none");
            });
            document.querySelectorAll('.line-mobile').forEach(function (element) {
              element.classList.add("d-none");
            });
          }
          else {
            document.querySelectorAll('.line-fix').forEach(function (element) {
              element.classList.add("d-none");
            });
            document.querySelectorAll('.line-mobile').forEach(function (element) {
              element.classList.remove("d-none");
            });
          }
        }

        return true;
      });
    }
  },
  computed: {
    sliderImages: function () {

      let card_images;
      let base_path_images = isUndefined(this.user.base_path_images) || isNull(this.user.base_path_images) ? '' : this.user.base_path_images;
      if (_.has(this.contestData, 'extra.images.slider')) {
        card_images = this.contestData.extra.images.slider.map(card_image => {
          return base_path_images + card_image
        });
      }
      else if (_.has(this.contestData, 'extra.images.featured')) {
        card_images = this.contestData.extra.images.featured.map(card_image => {
          return base_path_images + card_image
        });
      }
      else {
        card_images = [];
      }
      return card_images;
    },

    user_can_play: function () {
      if (((this.contestData.regulationInfo != undefined && this.contestData.regulationInfo != '') || (this.contestData.regulationUrl != undefined && this.contestData.regulationUrl != '')) && !this.acceptances.regulation ) {
        return false;
      }
      return !((this.contestData.privacyInfo !== undefined && this.contestData.privacyInfo !== '') && !this.acceptances.privacy);
    },

    is_last_day: function () {
      let now = Math.floor(new Date().getTime() / 1000);
      return (this.contestData.endDate - now) < (60 * 60 * 24);
    },

    countdown: function () {
      let now = Math.floor(new Date().getTime() / 1000);

      let result = {
        label: '',
        time: null
      };

      if (this.contestData) {

        if (this.contestData.startDate != undefined && now < this.contestData.startDate) {
          result.label = this.__('MANCANO SOLO');
          result.time  = this.contestData.startDate;
        }
        else if (this.contestData.endDate != undefined && now < this.contestData.endDate) {
          result.label = this.__('SCADE TRA');
          result.time  = this.contestData.endDate;
        }
      }
      return result;
    },

    getExternalURL: function () {
      if (this.current_contest.type === 'EXTERNAL') {
        let token_url_prefix = 'token';
        // let token_url_prefix = 'access_token';
        // if (this.doesContestHaveTokenInUrl()) {
        //   token_url_prefix = 'token';
        // }
        return this.contestData.externalUrl + '?' + token_url_prefix + '=' + this.accessToken
      }
      else {
        return false;
      }
    }
  },
  watch: {
    attempts_left: function () {
    },
    attempts_done: function () {
    },
    current_state: function () {
      // let self = this;
      // $(document).ready(function (){
      //   $('body #attempts-left1').html(self.attempts_left + (self.attempts_left > 1 ? ' giocate' : ' giocata'));
      //   $('body #attempts-left2').html(self.attempts_left + ( self.attempts_left > 1 ? ' giocate' : ' giocata'));
      // })
    },
    user: function () {
      this.chooseTypeAndElement();
    }
  }
}
</script>


